import type { Lang } from './types';

const LOCALE_MAP: Record<Lang, string> = {
  de: 'de-DE',
  en: 'en-US',
  fr: 'fr-FR',
  es: 'es-ES',
  it: 'it-IT',
  nl: 'nl-NL',
  pl: 'pl-PL',
};

function toLocale(lang: Lang): string {
  return LOCALE_MAP[lang] ?? LOCALE_MAP.de;
}

export function formatDate(
  date: Date | string | number,
  lang: Lang,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  return new Intl.DateTimeFormat(toLocale(lang), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...options,
  }).format(d);
}

export function formatRelativeDate(date: Date | string | number, lang: Lang): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    const labels: Record<string, string> = {
      de: 'Heute',
      en: 'Today',
      fr: "Aujourd'hui",
      es: 'Hoy',
      it: 'Oggi',
      nl: 'Vandaag',
      pl: 'Dzisiaj',
    };
    return labels[lang] ?? labels.de;
  }
  if (diffDays === 1) {
    const labels: Record<string, string> = {
      de: 'Gestern',
      en: 'Yesterday',
      fr: 'Hier',
      es: 'Ayer',
      it: 'Ieri',
      nl: 'Gisteren',
      pl: 'Wczoraj',
    };
    return labels[lang] ?? labels.de;
  }

  try {
    const rtf = new Intl.RelativeTimeFormat(toLocale(lang), { numeric: 'auto' });
    return rtf.format(-diffDays, 'day');
  } catch {
    return formatDate(d, lang);
  }
}

export function formatNumber(n: number, lang: Lang, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(toLocale(lang), options).format(n);
}

export function formatCurrency(amount: number, lang: Lang, currency = 'EUR'): string {
  return new Intl.NumberFormat(toLocale(lang), {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatPercent(value: number, lang: Lang, decimals = 0): string {
  return new Intl.NumberFormat(toLocale(lang), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
